import { atom } from 'jotai';
import { fetchApi } from '../../lib/apiClient';
import i18n from '../../lib/i18n';

export interface MembershipInfo {
  type: 'free' | '1month' | '6month' | '1year' | 'lifetime';
  isActive: boolean;
  expiresAt: string | null;
  grantedBy: string;
  grantedAt: string | null;
  notes: string | null;
}

// 会员信息数据
export const membershipDataAtom = atom<MembershipInfo | null>(null);

// 会员信息加载状态
export const membershipLoadingAtom = atom<boolean>(false);

// 会员信息错误状态
export const membershipErrorAtom = atom<string | null>(null);

// 获取会员信息的异步原子
export const fetchMembershipAtom = atom(
  null,
  async (get, set) => {
    set(membershipLoadingAtom, true);
    set(membershipErrorAtom, null);

    try {
      const membership = await fetchApi<MembershipInfo>('/membership/me');
      set(membershipDataAtom, membership);
      return membership;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '获取会员信息失败';
      set(membershipErrorAtom, errorMessage);
      console.error('获取会员信息失败:', error);
      throw error;
    } finally {
      set(membershipLoadingAtom, false);
    }
  }
);

// 检查是否为活跃会员的原子
export const isActiveMemberAtom = atom((get) => {
  const membership = get(membershipDataAtom);
  return membership?.isActive || false;
});

// 会员类型显示名称的原子
export const membershipTypeDisplayAtom = atom((get) => {
  const membership = get(membershipDataAtom);
  if (!membership) return i18n.t('v3/me:membership.status.unknown');

  const typeLabels = {
    free: i18n.t('v3/me:membership.status.free'),
    '1month': '1个月会员',
    '6month': '种子用户',
    '1year': '1年会员',
    lifetime: i18n.t('v3/me:membership.status.lifetime'),
  };

  return typeLabels[membership.type] || i18n.t('v3/me:membership.status.unknown');
});

// 会员到期状态的原子
export const membershipExpirationStatusAtom = atom((get) => {
  const membership = get(membershipDataAtom);
  if (!membership || !membership.isActive) {
    return { status: 'inactive', message: i18n.t('v3/me:membership.expiration.inactive') };
  }

  if (membership.type === 'lifetime') {
    return { status: 'lifetime', message: i18n.t('v3/me:membership.expiration.lifetime') };
  }

  if (!membership.expiresAt) {
    return { status: 'unknown', message: i18n.t('v3/me:membership.expiration.unknown') };
  }

  const expiresAt = new Date(membership.expiresAt);
  const now = new Date();
  const daysLeft = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (daysLeft < 0) {
    return { status: 'expired', message: i18n.t('v3/me:membership.expiration.expired') };
  } else if (daysLeft <= 7) {
    return { status: 'expiring', message: i18n.t('v3/me:membership.expiration.expiring', { days: daysLeft }) };
  } else if (daysLeft <= 30) {
    return { status: 'active', message: i18n.t('v3/me:membership.expiration.active', { days: daysLeft }) };
  } else {
    return { status: 'active', message: expiresAt.toLocaleDateString('zh-CN') + ' 到期' };
  }
});

// 会员来源显示的原子
export const membershipSourceDisplayAtom = atom((get) => {
  const membership = get(membershipDataAtom);
  if (!membership) return '';

  const sourceLabels = {
    invite_code: i18n.t('v3/me:membership.source.invite_code'),
    admin: i18n.t('v3/me:membership.source.admin'),
    migration: i18n.t('v3/me:membership.source.migration'),
    system: i18n.t('v3/me:membership.source.system'),
    stripe: i18n.t('v3/me:membership.source.stripe'),
    iap: i18n.t('v3/me:membership.source.iap'),
  };

  return sourceLabels[membership.grantedBy as keyof typeof sourceLabels] || membership.grantedBy;
});
